/**
 * ClusterMarker
 *
 * Map marker component that renders either a cluster badge or an individual guard marker.
 * Clusters show the count of guards and have alert styling when containing problematic guards.
 *
 * @see Issue 7.1: Implement marker clustering
 */

import React, { memo } from 'react';
import { Typography } from '@mui/material';
import classNames from 'classnames';
import styles from './ClusterMarker.module.css';
import { ClusterOrPoint, isCluster, GuardLocation } from './types';
import GuardMarker from './GuardMarker';

interface ClusterMarkerProps {
  /** The cluster or individual point feature from supercluster */
  feature: ClusterOrPoint;
  /** For non-cluster points, the full guard data for popup display */
  guard?: GuardLocation;
  /** Latitude for google-map-react positioning */
  lat: number;
  /** Longitude for google-map-react positioning */
  lng: number;
  /** Callback when cluster or marker is clicked */
  onClick: (feature: ClusterOrPoint) => void;
  /** Whether this marker is currently selected (non-cluster only) */
  isSelected?: boolean;
  /** Whether the popup should be shown (non-cluster only) */
  showPopup?: boolean;
  /** Callback when "View Details" is clicked (non-cluster only) */
  onViewDetails?: (guardId: number) => void;
  /** Callback to close the popup (non-cluster only) */
  onClosePopup?: () => void;
  /** Whether the trail is visible for this guard (non-cluster only) */
  showTrail?: boolean;
  /** Callback when trail visibility is toggled (non-cluster only) */
  onToggleTrail?: (guardId: number) => void;
}

/**
 * Renders either a cluster badge (for grouped guards) or delegates to
 * GuardMarker for individual guard points.
 */
const ClusterMarker: React.FC<ClusterMarkerProps> = ({
  feature,
  guard,
  lat,
  lng,
  onClick,
  isSelected,
  showPopup,
  onViewDetails,
  onClosePopup,
  showTrail,
  onToggleTrail,
}) => {
  // If not a cluster, render the normal GuardMarker component
  if (!isCluster(feature)) {
    if (!guard) {
      // Guard data required for non-cluster rendering
      return null;
    }

    return (
      <GuardMarker
        guard={guard}
        lat={lat}
        lng={lng}
        isSelected={isSelected}
        showPopup={showPopup}
        onClick={() => onClick(feature)}
        onViewDetails={onViewDetails}
        onClosePopup={onClosePopup}
        showTrail={showTrail}
        onToggleTrail={onToggleTrail}
      />
    );
  }

  // Cluster rendering
  const count = feature.properties.point_count;
  // hasAlert is aggregated as a count (number of guards with alerts in cluster)
  const alertCount = (feature.properties as any).hasAlert ?? 0;
  const hasAlert = alertCount > 0;

  // Determine size class based on guard count
  let sizeClass = styles.small;
  if (count > 50) {
    sizeClass = styles.large;
  } else if (count > 10) {
    sizeClass = styles.medium;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick(feature);
  };

  return (
    <div
      className={classNames(styles.cluster, sizeClass, {
        [styles.hasAlert]: hasAlert,
      })}
      onClick={handleClick}
      title={`${count} guards${hasAlert ? ` (${alertCount} need${alertCount === 1 ? 's' : ''} attention)` : ''}`}
    >
      <Typography className={styles.count}>{count}</Typography>
    </div>
  );
};

/**
 * Custom comparison function for React.memo
 * Only re-render when values that actually affect the marker change
 */
function arePropsEqual(
  prevProps: ClusterMarkerProps,
  nextProps: ClusterMarkerProps
): boolean {
  // Compare UI state
  if (
    prevProps.isSelected !== nextProps.isSelected ||
    prevProps.showPopup !== nextProps.showPopup ||
    prevProps.showTrail !== nextProps.showTrail
  ) {
    return false;
  }

  // For clusters, compare cluster properties
  const prevFeature = prevProps.feature;
  const nextFeature = nextProps.feature;

  if (isCluster(prevFeature) && isCluster(nextFeature)) {
    // Compare cluster ID and count
    if (
      prevFeature.properties.cluster_id !== nextFeature.properties.cluster_id ||
      prevFeature.properties.point_count !== nextFeature.properties.point_count ||
      (prevFeature.properties as any).hasAlert !== (nextFeature.properties as any).hasAlert
    ) {
      return false;
    }
    // Position
    if (
      prevFeature.geometry.coordinates[0] !== nextFeature.geometry.coordinates[0] ||
      prevFeature.geometry.coordinates[1] !== nextFeature.geometry.coordinates[1]
    ) {
      return false;
    }
    return true;
  }

  // For individual guards, compare guard data
  if (!isCluster(prevFeature) && !isCluster(nextFeature)) {
    // Guard ID must match
    if (prevFeature.properties.guardId !== nextFeature.properties.guardId) {
      return false;
    }

    // Compare guard object if present (by relevant values)
    const prevGuard = prevProps.guard;
    const nextGuard = nextProps.guard;

    if (!prevGuard && !nextGuard) return true;
    if (!prevGuard || !nextGuard) return false;

    if (
      prevGuard.guardId !== nextGuard.guardId ||
      prevGuard.status !== nextGuard.status ||
      prevGuard.latitude !== nextGuard.latitude ||
      prevGuard.longitude !== nextGuard.longitude ||
      prevGuard.batteryPercent !== nextGuard.batteryPercent ||
      prevGuard.lastSeenFormatted !== nextGuard.lastSeenFormatted ||
      prevGuard.currentPatrol?.patrolId !== nextGuard.currentPatrol?.patrolId ||
      prevGuard.currentPatrol?.patrolStatus !== nextGuard.currentPatrol?.patrolStatus ||
      prevGuard.currentPatrol?.checkpointsCompleted !== nextGuard.currentPatrol?.checkpointsCompleted
    ) {
      return false;
    }

    return true;
  }

  // One is cluster, one is not - definitely different
  return false;
}

export default memo(ClusterMarker, arePropsEqual);
